import React from 'react';
import { Mail, AlertCircle } from 'lucide-react';

interface EmailVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onResendVerification: () => void;
  email: string;
  onEmailChange?: (email: string) => void;
  showEmailInput?: boolean;
  isEmailKnownToExist?: boolean;
  isLoading: boolean;
}

const EmailVerificationModal: React.FC<EmailVerificationModalProps> = ({
  isOpen,
  onClose,
  onResendVerification,
  email,
  onEmailChange,
  showEmailInput = false,
  isEmailKnownToExist = false,
  isLoading
}) => {
  if (!isOpen) return null;

  const hasEmail = !!email?.trim();
  const showKnownEmailCopy = hasEmail && isEmailKnownToExist;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-in fade-in-0 zoom-in-95 duration-200">
        <div className="flex items-center justify-center w-12 h-12 mx-auto bg-yellow-100 rounded-full mb-4">
          <AlertCircle className="h-6 w-6 text-yellow-600" />
        </div>
        
        <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
          {showKnownEmailCopy ? 'Email Verification Required' : 'Resend verification email'}
        </h3>
        
        <p className="text-sm text-gray-600 text-center mb-4">
          {showKnownEmailCopy ? (
            <>
              An account was registered with the email <span className="font-medium">{email}</span>, but the email has not been verified.
            </>
          ) : (
            <>Enter your email address and we’ll send you a new verification email.</>
          )}
        </p>

        {showEmailInput && (
          <div className="mb-6">
            <label htmlFor="verificationEmail" className="block text-sm font-medium text-gray-700 mb-2">
              Email address
            </label>
            <input
              id="verificationEmail"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => onEmailChange?.(e.target.value)}
              placeholder="Enter your email"
              className="aacore-textfield-primary"
            />
          </div>
        )}
        
        <p className="text-sm text-gray-600 text-center mb-6">
          {showKnownEmailCopy ? 'Would you like us to send another verification email?' : 'Send a verification email to this address?'}
        </p>
        
        <div className="flex space-x-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="aacore-btn2-secondary flex-1 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            No, thanks
          </button>
          
          <button
            onClick={onResendVerification}
            disabled={isLoading || (showEmailInput && !email.trim())}
            className="aacore-btn2-primary flex-1 justify-center disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Sending...
              </>
            ) : (
              <>
                <Mail className="h-4 w-4 mr-2" />
                Yes, resend
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationModal; 